import { AnchoredPopoverComponent } from '@theme/anchored-popover';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom quantity picker for the cart drawer rendered as an anchored popover:
 * a "Qty N" trigger and a floating panel of numbers. A native <select> can't be
 * styled to the design (centred serif numbers in a bordered panel), so this uses
 * the Popover API (free light-dismiss, not clipped by the drawer's overflow) via
 * {@link AnchoredPopoverComponent} for open/close + positioning.
 *
 * Choosing an option dispatches a {@link QuantitySelectorUpdateEvent} — the same
 * event the +/- stepper raises — which <cart-items-component> handles to update
 * the line through the cart Section Rendering API, reusing the existing update,
 * debounce and error flow.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} popover
 * @property {HTMLElement} trigger
 * @property {HTMLElement} [label]
 *
 * @extends {AnchoredPopoverComponent}
 */
export class CartQuantityDropdownComponent extends AnchoredPopoverComponent {
  /**
   * Sets the line quantity from the chosen option and closes the panel.
   * @param {Event} event
   */
  selectQuantity(event) {
    const option = event.target;
    if (!(option instanceof HTMLElement)) return;

    const quantity = parseInt(option.getAttribute('value') ?? '', 10);
    if (Number.isNaN(quantity)) return;

    const cartLine = Number(option.dataset.cartLine) || undefined;

    // Optimistically reflect the choice in the trigger before the cart re-renders.
    const { label, popover } = this.refs;
    if (label) {
      const prefix = this.dataset.qtyPrefix ?? '';
      label.textContent = `${prefix} ${quantity}`.trim();
    }

    if (popover?.matches(':popover-open')) popover.hidePopover();

    this.dispatchEvent(new QuantitySelectorUpdateEvent(quantity, cartLine));
  }
}

if (!customElements.get('cart-quantity-dropdown-component')) {
  customElements.define('cart-quantity-dropdown-component', CartQuantityDropdownComponent);
}
